import type { RawRow } from "../types";
import type { WarningCollector } from "../warnings/warning-collector";
import { cellText, isMeaningful, rowMap } from "./cell-utils";
import { RowClassifier } from "./row-classifier";
import type { ParsedItem, ParsedItemContext } from "./types";

/**
 * セクションごとの期待カラム (説明タブ準拠)。
 * `content` 行がここに列挙された全カラムを含む場合のみ、その行をヘッダーと認識する。
 *
 * 期待カラムは「順序問わず subset として包含」されていれば OK (列順入れ替え / 余分な列追加は許容)。
 * 1 つでも欠けているとヘッダーと認識されず、当該セクションのデータは抽出されない。
 */
const SECTION_EXPECTED_HEADERS: Record<string, readonly string[]> = {
  項目定義: [
    "項目定義No.",
    "項目名",
    "表示名",
    "表示条件",
    "親項目",
    "入力フォーム分類",
    "オブジェクト種別",
    "I/O",
    "DBマッピング",
    "繰り返し要素",
    "参照コンポーネント",
    "備考",
  ],
  データバリデーション: [
    "データバリデーションNo.",
    "項目定義No.",
    "項目名",
    "必須",
    "初期値",
    "最小値",
    "最大値",
    "パターン",
  ],
  画面遷移: [
    "画面遷移No.",
    "項目定義No.",
    "項目名",
    "画面ID",
    "遷移先画面名",
    "遷移タイプ",
    "実行条件",
  ],
  振る舞い: [
    "振る舞いNo.",
    "項目定義No.",
    "項目名",
    "概要",
    "実行条件",
    "処理内容",
    "正常終了時",
    "エラー発生時",
  ],
  メッセージ: [
    "メッセージNo.",
    "項目定義No.",
    "表示場所（項目名）",
    "メッセージID",
    "メッセージ内容",
    "メッセージ表示条件",
  ],
};

const HEADER_ALIASES = new Map<string, string>([
  ["最小桁数", "最小値"],
  ["最大桁数", "最大値"],
  ["遷移先画面ID", "画面ID"],
]);

/**
 * 詳細シート (`画面_*` / `共通_*`) 1 枚を解析し `ParsedItemContext` を返す。
 * シート間の紐付け (画面ID で View と結合 等) は `TargetBuilder` の責務。
 */
export class DetailSheetParser {
  private readonly classifier = new RowClassifier();

  constructor(private readonly warnings: WarningCollector) {}

  parse(
    sheetName: string,
    rows: RawRow[],
    kind: "view" | "component",
  ): ParsedItemContext {
    const metadata: Record<string, string> = {};
    let currentSection = "";
    let currentHeader: string[] = [];
    // Map を使用 (Object.prototype の `toString` 等を `項目定義No.` 値として誤参照しないため)
    const itemsByNo = new Map<string, ParsedItem>();
    const orderedNos: string[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] ?? [];
      const rowIndex = i + 1;
      const rowKind = this.classifier.classify(row);

      if (rowKind === "blank") continue;

      if (rowKind === "metadata") {
        const k = cellText(row[0]);
        metadata[k] = cellText(row[1]);
        currentSection = "";
        currentHeader = [];
        continue;
      }

      if (rowKind === "section") {
        currentSection = cellText(row[0]);
        currentHeader = [];
        continue;
      }

      // content: セクション未確定なら捨てる (タイトル / コメント等)
      if (!currentSection) continue;

      // ヘッダー未確定: 期待カラムが全て揃っていればヘッダーとして採用
      if (currentHeader.length === 0) {
        const expected = requiredHeadersFor(currentSection, kind);
        if (!expected) continue;
        const normalizedHeader = row.map((c) => normalizeHeaderName(cellText(c)));
        const cells = new Set(normalizedHeader);
        if (expected.every((h) => cells.has(h))) {
          currentHeader = normalizedHeader;
        }
        continue;
      }

      // データ行: ヘッダー名で値を引く (列順入れ替えに追従)。
      // No. / 項目定義No. 欠落行 (コメント / 集計行等) は applyDataRow 内で静かに skip される。
      const v = rowMap(currentHeader, row);
      this.applyDataRow(
        currentSection,
        v,
        rowIndex,
        itemsByNo,
        orderedNos,
        sheetName,
      );
    }

    const items: ParsedItem[] = orderedNos
      .map((no) => itemsByNo.get(no))
      .filter((it): it is ParsedItem => it !== undefined);

    return {
      contextKind: kind,
      contextId:
        kind === "view"
          ? (metadata["画面ID"] ?? "")
          : (metadata["共通コンポーネントID"] ?? ""),
      contextName:
        kind === "view"
          ? (metadata["画面名"] ?? "")
          : (metadata["共通コンポーネント名"] ?? ""),
      serviceId: metadata["サービスID"] ?? "",
      serviceName: metadata["サービス名"] ?? "",
      path: kind === "view" ? metadata["パス"] : undefined,
      conditions: kind === "view" ? splitConditions(metadata["表示条件"]) : [],
      items,
      sheetName,
    };
  }

  private applyDataRow(
    section: string,
    values: Record<string, string>,
    sourceRow: number,
    itemsByNo: Map<string, ParsedItem>,
    orderedNos: string[],
    sheetName: string,
  ): void {
    if (section === "項目定義") {
      const no = normalizeNo(values["項目定義No."] ?? "");
      // No. 欠落行 (コメント / 集計行) は静かに skip
      if (!no) return;
      // 項目名が空欄でも No. 重複は出力に影響するため、name 検査より前に通知する。
      if (itemsByNo.has(no)) {
        this.warnings.add({
          code: "duplicate_item_no_in_sheet",
          message: `項目定義No.="${no}" is duplicated within the sheet; keeping the first row and discarding subsequent rows.`,
          location: { sheetName, rowIndex: sourceRow, columnName: "項目定義No." },
          context: { no, name: values["項目名"] ?? "" },
        });
        return;
      }
      const name = values["項目名"] ?? "";
      if (!name) return;
      const formCategoryRaw = (values["入力フォーム分類"] ?? "").trim();
      const displayTextRaw = values["表示名"] ?? "";
      const item: ParsedItem = {
        no,
        name,
        displayText: isMeaningful(displayTextRaw) ? displayTextRaw : undefined,
        conditions: splitConditions(values["表示条件"]),
        parentItemRef: (values["親項目"] ?? "").trim() || undefined,
        formCategory: formCategoryRaw || undefined,
        objectType: (values["オブジェクト種別"] ?? "").trim(),
        io: (values["I/O"] ?? "").trim() || undefined,
        dbMapping: parseDbMapping(values["DBマッピング"]),
        isTemplate: parseBool(values["繰り返し要素"]),
        referenceComponentId: (values["参照コンポーネント"] ?? "").trim() || undefined,
        description: values["備考"] ?? "",
        transitions: [],
        behaviors: [],
        messages: [],
        sourceRow,
      };
      itemsByNo.set(no, item);
      orderedNos.push(no);
      return;
    }

    // 以下は 項目定義No. で項目定義に集約するセクション
    const refNo = normalizeNo(values["項目定義No."] ?? "");
    if (!refNo) {
      // 項目定義No. 列が空欄 → セクション継続行 / 集計行等の可能性が高く、参照ミスではない。
      // ミスリードな `unresolved_item_ref ("項目定義No.='' が見つからない")` を避けて静かに skip。
      return;
    }
    const refItem = itemsByNo.get(refNo);
    if (!refItem) {
      this.warnings.add({
        code: "unresolved_item_ref",
        message: `${section} references 項目定義No.="${refNo}", which does not exist; skipping this section row.`,
        location: { sheetName, rowIndex: sourceRow, columnName: "項目定義No." },
        context: { section, refNo },
      });
      return;
    }

    if (section === "データバリデーション") {
      refItem.validation = {
        required: parseBool(values["必須"]),
        defaultValue: optional(values["初期値"]),
        minLength: optional(values["最小値"]),
        maxLength: optional(values["最大値"]),
        pattern: optional(values["パターン"]),
      };
      return;
    }

    if (section === "画面遷移") {
      refItem.transitions.push({
        targetScreenId: values["画面ID"] ?? "",
        targetScreenName: values["遷移先画面名"] ?? "",
        transitionType: values["遷移タイプ"] ?? "",
        condition: values["実行条件"] ?? "",
      });
      return;
    }

    if (section === "振る舞い") {
      refItem.behaviors.push({
        summary: values["概要"] ?? "",
        precondition: values["実行条件"] ?? "",
        processContent: values["処理内容"] ?? "",
        expectedChangeOnSuccess: values["正常終了時"] ?? "",
        expectedChangeOnError: values["エラー発生時"] ?? "",
      });
      return;
    }

    if (section === "メッセージ") {
      refItem.messages.push({
        messageId: values["メッセージID"] ?? "",
        content: values["メッセージ内容"] ?? "",
        condition: values["メッセージ表示条件"] ?? "",
      });
      return;
    }
  }
}

/**
 * ヘッダー認識に使う必須カラム集合を返す。
 * `共通_*` シート (kind="component") の `項目定義` セクションでは
 * 「参照コンポーネント」列を任意とし、列が無くてもヘッダーとして認識する。
 * `画面_*` シート (kind="view") は従来どおり必須のまま。
 */
function requiredHeadersFor(
  section: string,
  kind: "view" | "component",
): readonly string[] | undefined {
  const expected = SECTION_EXPECTED_HEADERS[section];
  if (!expected) return undefined;
  if (kind === "component" && section === "項目定義") {
    return expected.filter((h) => h !== "参照コンポーネント");
  }
  return expected;
}

function normalizeHeaderName(header: string): string {
  return HEADER_ALIASES.get(header) ?? header;
}

/**
 * 仕様書テンプレートの No. 列値を比較可能な形に正規化。
 *
 * - 前後の空白を除去
 * - 末尾 `.0` / `.00` 等 (Excel が数値セルを文字列化する際の `1.0` 形式) を除去
 * - 先頭の `0` 連続を除去 (`01` → `1`、ただし `0` は `0` のまま)
 *
 * パターンに合致しない場合は元値をそのまま返す (英数記号混在の ID 等を保持)。
 */
function normalizeNo(value: string): string {
  const trimmed = value.trim();
  const m = trimmed.match(/^(\d+)(?:\.0+)?$/);
  if (!m) return trimmed;
  const stripped = m[1].replace(/^0+(?=\d)/, "");
  return stripped;
}

function parseBool(raw: string | undefined): boolean | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toUpperCase();
  if (v === "TRUE") return true;
  if (v === "FALSE") return false;
  return undefined;
}

function optional(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  return isMeaningful(v) ? v : undefined;
}

function splitConditions(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(isMeaningful);
}

/**
 * `DBマッピング` 列の解析。1 行 = 1 エントリ、`テーブル名・カラム名` (`・` / `.` / `>` 区切り)。
 * パース不能な行はスキップ。
 */
function parseDbMapping(raw: string | undefined): { table: string; column: string }[] {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(isMeaningful)
    .map((s) => s.split(/[・.>＞]/, 2))
    .filter((parts): parts is [string, string] => parts.length === 2 && parts[0].length > 0 && parts[1].length > 0)
    .map(([table, column]) => ({ table: table.trim(), column: column.trim() }));
}
